import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { Button } from '@trello/nachos/button';
import type { PopoverProps } from '@trello/nachos/popover';
import { Popover, PopoverPlacement, usePopover } from '@trello/nachos/popover';

import styles from './WorkspaceJoinRequestPopover.less';

export const useWorkspaceJoinRequestPopover = () => {
  const {
    triggerRef: triggerRefApproveWorkspaceJoinRequestPopover,
    toggle: toggleApproveWorkspaceJoinRequestPopover,
    hide: hideApproveWorkspaceJoinRequestPopover,
    popoverProps: approveWorkspaceJoinRequestPopoverProps,
  } = usePopover<HTMLButtonElement>();

  const {
    triggerRef: triggerRefDeletePopover,
    toggle: toggleDeletePopover,
    hide: hideDeletePopover,
    popoverProps: deletePopoverProps,
  } = usePopover<HTMLButtonElement>();

  return {
    triggerRefApproveWorkspaceJoinRequestPopover,
    toggleApproveWorkspaceJoinRequestPopover,
    hideApproveWorkspaceJoinRequestPopover,
    approveWorkspaceJoinRequestPopoverProps,
    triggerRefDeletePopover,
    toggleDeletePopover,
    hideDeletePopover,
    deletePopoverProps,
  };
};

type OrgType = 'bc' | 'enterprise' | 'free' | 'standard';

interface PopoverContentsProps {
  isDelete: boolean;
  isMultipleSelected: boolean;
  orgType: OrgType;
}

const PopoverContent: FunctionComponent<PopoverContentsProps> = ({
  isDelete,
  isMultipleSelected,
  orgType,
}) => {
  const intl = useIntl();

  let teamType = '';

  switch (orgType) {
    case 'bc':
      teamType = intl.formatMessage({
        id: 'templates.workspace_join_requests.bc',
        defaultMessage: 'Premium',
        description: 'Premium',
      });
      break;
    case 'standard':
      teamType = intl.formatMessage({
        id: 'templates.workspace_join_requests.standard',
        defaultMessage: 'Standard',
        description: 'Standard',
      });
      break;
    case 'enterprise':
      teamType = intl.formatMessage({
        id: 'templates.workspace_join_requests.enterprise',
        defaultMessage: 'Enterprise',
        description: 'Enterprise',
      });
      break;
    default:
      break;
  }

  if (isDelete) {
    if (isMultipleSelected) {
      return (
        <FormattedMessage
          id="templates.workspace_join_requests.delete-pending-requests-to-join-this-workspace"
          defaultMessage="Delete pending requests to join this Workspace. This can’t be undone."
          description="Popover content for deleting multiple workspace join requests"
          tagName="p"
        />
      );
    }
    return (
      <FormattedMessage
        id="templates.workspace_join_requests.delete-this-pending-request-to-join-this-workspace"
        defaultMessage="Delete this pending request to join this Workspace. This can’t be undone."
        description="Popover content for deleting a single workspace join request"
        tagName="p"
      />
    );
  }
  return (
    <FormattedMessage
      id="templates.workspace_join_requests.adding-people-to-your-workspace-will-update-your-billing-automatically"
      defaultMessage="Adding people to your {teamType} Workspace will update your billing automatically."
      description="Popover content for adding a workspace join request"
      tagName="p"
      values={{ teamType }}
    />
  );
};

interface WorkspaceJoinRequestPopoverProps extends PopoverProps {
  onClick: () => void;
  isMultipleSelected: boolean;
  isDelete: boolean;
  orgId: string;
  orgType: OrgType;
}

export const WorkspaceJoinRequestPopover: FunctionComponent<
  WorkspaceJoinRequestPopoverProps
> = ({
  onClick,
  onHide,
  isMultipleSelected,
  isDelete,
  isVisible,
  orgType,
  orgId,
  ...popoverProps
}) => {
  useEffect(() => {
    if (isVisible) {
      Analytics.sendScreenEvent({
        name: isDelete
          ? 'deletePendingMembersInlineDialog'
          : 'approvePendingMembersInlineDialog',
        containers: formatContainers({ workspaceId: orgId }),
        attributes: {
          isMultipleSelected,
        },
      });
    }
  }, [isDelete, isMultipleSelected, orgId, isVisible]);

  const popoverTitle = isDelete ? (
    <FormattedMessage
      id="templates.workspace_join_requests.delete-request"
      defaultMessage="Delete request"
      description="Popover title for deleting a workspace join request"
    />
  ) : (
    <FormattedMessage
      id="templates.workspace_join_requests.add-to-workspace"
      defaultMessage="Add to Workspace"
      description="Popover title for adding a workspace join request"
    />
  );

  return (
    <Popover
      {...popoverProps}
      isVisible={isVisible}
      placement={PopoverPlacement.BOTTOM_START}
      onHide={onHide}
      title={popoverTitle}
    >
      <PopoverContent
        isDelete={isDelete}
        isMultipleSelected={isMultipleSelected}
        orgType={orgType}
      />
      <Button
        className={styles.confirmationButton}
        onClick={onClick}
        appearance={isDelete ? 'danger' : 'primary'}
        size="fullwidth"
      >
        {isDelete ? (
          <FormattedMessage
            id="templates.workspace_join_requests.delete"
            defaultMessage="Delete"
            description="Delete button text in the workspace join request popover"
          />
        ) : (
          <FormattedMessage
            id="templates.workspace_join_requests.add-to-workspace"
            defaultMessage="Add to Workspace"
            description="Add to workspace button text in the workspace join request popover"
          />
        )}
      </Button>
    </Popover>
  );
};
